import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': process.env.REACT_APP_API_KEY,
  },
});

const authApi = axios.create({
  baseURL: process.env.REACT_APP_AUTH_URL,
  headers: { 'Content-Type': 'application/json' },
});

export const authService = {
  sendOtp: async (email) => {
    const response = await authApi.post('/v1/auth/email/send-otp', { email });
    return response.data;
  },

  verifyOtp: async (email, otp) => {
    const response = await authApi.post('/v1/auth/email/verify-otp', { email, otp });
    return response.data;
  },

  sendPhoneOtp: async (phone) => {
    const response = await authApi.post('/v1/auth/phone/send-otp', { phone });
    return response.data;
  },

  verifyPhoneOtp: async (phone, code) => {
    const response = await authApi.post('/v1/auth/phone/verify-otp', { phone, code });
    return response.data;
  },
};


export const jobApplicationService = {
  // Obtener token temporal de acceso
  getToken: async () => {
    const response = await authApi.post('/v1/job-applications/token');
    return response.data;
  },

  // Crear nueva solicitud
  create: async (data, token) => {
    const response = await authApi.post('/v1/job-applications', data, {
      headers: { 'X-Application-Token': token },
    });
    return response.data;
  },

  // Subir un documento con frontal y reverso
  uploadDocumentWithSides: async (applicationId, documentType, frontImage, backImage, token) => {
    const formData = new FormData();
    formData.append('document_type', documentType);
    formData.append('front_image', frontImage);
    formData.append('back_image', backImage);

    const response = await authApi.post(
      `/v1/job-applications/${applicationId}/upload-documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Application-Token': token,
        },
      }
    );
    return response.data;
  },

  // Rollback: borra la solicitud si quedó incompleta. El backend exige la
  // cédula y el correo de la solicitud para permitir el borrado.
  deleteApplication: async (applicationId, owner, token) => {
    const response = await authApi.delete(`/v1/job-applications/${applicationId}`, {
      headers: { 'X-Application-Token': token },
      data: { national_id: owner.national_id, email: owner.email },
    });
    return response.data;
  },

  // Subir la foto tamano carne (una sola imagen, sin reverso)
  uploadPhoto: async (applicationId, photoImage, token) => {
    const formData = new FormData();
    formData.append('document_type', 'photo');
    formData.append('file', photoImage);

    const response = await authApi.post(
      `/v1/job-applications/${applicationId}/upload-document`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Application-Token': token,
        },
      }
    );
    return response.data;
  },

  // Subir múltiples documentos (3 llamadas separadas, una por documento)
  uploadDocuments: async (applicationId, documents, token) => {
    const results = {};
    const uploadPromises = [];

    // Cédula
    if (documents.national_id?.front && documents.national_id?.back) {
      uploadPromises.push(
        jobApplicationService.uploadDocumentWithSides(
          applicationId,
          'national_id',
          documents.national_id.front,
          documents.national_id.back,
          token
        ).then(result => { results.national_id = result; })
      );
    }

    // Licencia
    if (documents.license?.front && documents.license?.back) {
      uploadPromises.push(
        jobApplicationService.uploadDocumentWithSides(
          applicationId,
          'license',
          documents.license.front,
          documents.license.back,
          token
        ).then(result => { results.license = result; })
      );
    }

    // Matrícula
    if (documents.registration?.front && documents.registration?.back) {
      uploadPromises.push(
        jobApplicationService.uploadDocumentWithSides(
          applicationId,
          'registration',
          documents.registration.front,
          documents.registration.back,
          token
        ).then(result => { results.registration = result; })
      );
    }

    // Ejecutar todas las subidas en paralelo
    await Promise.all(uploadPromises);

    return {
      success: true,
      data: results,
      message: 'Documentos subidos exitosamente'
    };
  },

  // Validar documentos con OCR
  validateDocuments: async (documentType, nationalId, frontImage, backImage = null, token) => {
    const formData = new FormData();
    formData.append('document_type', documentType);
    formData.append('national_id', nationalId);
    formData.append('front_image', frontImage);
    if (backImage) {
      formData.append('back_image', backImage);
    }

    const response = await authApi.post(
      '/v1/job-applications/validate-documents',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Application-Token': token,
        },
      }
    );
    return response.data;
  },
};

const WEEK_HOURS = (start, end) =>
  ['L','M','X','J','V','S','D'].map((day) => ({ day, start, end }));

const CITIES_MOCK = [
  { id: 1, name: 'Ibarra',    latitude: 0.3516, longitude: -78.1222, status: 'active', meeting_area_stores: 500,  min_hours: 30, operating_hours: WEEK_HOURS('08:00','23:30') },
  { id: 2, name: 'Atuntaqui', latitude: 0.3382, longitude: -78.2126, status: 'active', meeting_area_stores: 4000, min_hours: 2,  operating_hours: WEEK_HOURS('08:00','22:00') },
];

export const citiesService = {
  getAll: async () => {
    try {
      const response = await authApi.get('/v1/onboarding/cities');
      const raw = response.data?.data ?? response.data ?? [];
      return { data: Array.isArray(raw) ? raw : [] };
    } catch {
      return { data: CITIES_MOCK };
    }
  },
};

const BANKS_MOCK = [
  { id: 1,  name: 'B. Pichincha',        is_active: 1, transfer_comission: 0.45, cod_bank: '10' },
  { id: 2,  name: 'B. Produbanco',       is_active: 1, transfer_comission: 0.45, cod_bank: '36' },
  { id: 3,  name: 'B. Guayaquil',        is_active: 1, transfer_comission: 0.45, cod_bank: '17' },
  { id: 4,  name: 'B. Pacífico',         is_active: 1, transfer_comission: 0.45, cod_bank: '22' },
  { id: 5,  name: 'B. Internacional',    is_active: 1, transfer_comission: 0.45, cod_bank: '32' },
  { id: 6,  name: 'B. Bolivariano',      is_active: 1, transfer_comission: 0.45, cod_bank: '07' },
  { id: 7,  name: 'B. del Austro',       is_active: 1, transfer_comission: 0.45, cod_bank: '03' },
  { id: 8,  name: 'B. Solidario',        is_active: 1, transfer_comission: 0.45, cod_bank: '39' },
  { id: 9,  name: 'BanEcuador',          is_active: 1, transfer_comission: 0.45, cod_bank: '47' },
  { id: 10, name: 'Mutualista Pichincha', is_active: 1, transfer_comission: 0.45, cod_bank: '62' },
];

export const banksService = {
  getAll: async (cityId) => {
    try {
      const response = await authApi.get('/v1/onboarding/banks', { params: { city_id: cityId } });
      const raw = response.data?.data ?? response.data ?? [];
      return { data: Array.isArray(raw) ? raw : [] };
    } catch {
      return { data: BANKS_MOCK };
    }
  },
};

export const restaurantCategoriesService = {
  getAll: async () => {
    try {
      const response = await authApi.get('/v1/onboarding/categories');
      const raw = response.data?.data ?? response.data ?? [];
      return { data: Array.isArray(raw) ? raw : [] };
    } catch {
      return { data: [] };   /* fallback → StepRestaurantCategory keeps su mock */
    }
  },
};

/* ── Convierte data-URL base64 a Blob para enviar como archivo ── */
const base64ToBlob = (dataUrl) => {
  const [meta, b64] = dataUrl.split(',');
  const mime = meta.match(/:(.*?);/)[1];
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

export const storeOwnerService = {
  register: async (formData) => {
    const body = {
      email:          formData.contact_email,
      mobile:         formData.mobile,
      name:           formData.representative_name,
      last_name:      formData.representative_last_name,
      terms_accepted: true,
    };
    const response = await api.post('/public/store/register', body);
    return response.data;
  },
};

export const storeService = {
  create: async (formData) => {
    const fd = new FormData();

    // ── Identidad ──────────────────────────────────────────────
    fd.append('name',        formData.name);
    fd.append('description', formData.description);

    // Foto: viene como data-URL → convertir a Blob
    if (formData.photo) {
      const blob = base64ToBlob(formData.photo);
      const ext  = blob.type.split('/')[1] || 'jpg';
      fd.append('photo', blob, `logo.${ext}`);
    }

    // ── Categoría ──────────────────────────────────────────────
    fd.append('category_id',    formData.category_id);
    fd.append('subcategory_id', formData.subcategory_id);

    // ── Contacto ───────────────────────────────────────────────
    fd.append('contact_email', formData.contact_email);
    const mobile = '+593' + (formData.mobile || '').replace(/\D/g, '').replace(/^0+/, '');
    fd.append('mobile', mobile);
    if (formData.estimated_time)
      fd.append('estimated_time', formData.estimated_time);

    // ── Ciudad y ubicación ─────────────────────────────────────
    fd.append('city_id',      formData.city_id);
    fd.append('latitude',     formData.latitude);
    fd.append('longitude',    formData.longitude);
    fd.append('address_main', formData.address_main);
    fd.append('address_cross', formData.address_cross);
    fd.append('address_ref',  formData.address_ref);

    // ── Plan ───────────────────────────────────────────────────
    fd.append('exclusive', formData.exclusive ? 1 : 0);

    // ── Horario ────────────────────────────────────────────────
    fd.append('schedule', formData.schedule);

    // ── Banco ──────────────────────────────────────────────────
    fd.append('bank_id',             formData.bank_id);
    fd.append('bank_account_type',   formData.bank_account_type);
    fd.append('bank_account_number', formData.bank_account_number);
    fd.append('bank_owner_name',     formData.bank_owner_name);
    fd.append('bank_ci_type',        formData.bank_ci_type);
    fd.append('bank_ci_number',      formData.bank_ci_number);

    // ── Facturación ────────────────────────────────────────────
    fd.append('is_billing_enabled',      1);
    fd.append('business_name',           formData.business_name);
    fd.append('ruc',                     formData.ruc);
    fd.append('billing_address',         formData.billing_address);
    fd.append('representative_name',     formData.representative_name);
    fd.append('representative_last_name', formData.representative_last_name);
    fd.append('representative_dni',      formData.representative_dni);

    // Certificado RUC (File object directo)
    if (formData.ruc_certificate) {
      fd.append('ruc_certificate', formData.ruc_certificate);
    }

    // ── Términos ───────────────────────────────────────────────
    fd.append('terms_accepted', 1);

    const response = await authApi.post('/v1/onboarding/store', fd, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

export default api;
